/**
 * NewProvisionComponent - Modernized for Angular 20+
 * Full-page form for creating and editing Provisions.
 * Follows the same patterns as NewPersonFullPageComponent.
 */
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { CommonModule } from '@angular/common';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSliderModule } from '@angular/material/slider';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';

// PrimeNG imports
import { EditorModule } from 'primeng/editor';

// Services
import { ProvisionsService } from '../../service/entity-services/provisions.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { ProvisionSheetsService } from '../../service/google-sheets/provision-sheets.service';

// Objects
import { Provision } from '../../objects/provision';
import { EntityType } from '../../objects/interface-library';
import { CreateEntity } from '../create-entity';

@Component({
  selector: 'app-new-provision',
  templateUrl: './new-provision.component.html',
  styleUrls: ['./new-provision.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatDatepickerModule,
    MatSliderModule,
    MatChipsModule,
    MatDialogModule,
    EditorModule
  ]
})
export class NewProvisionComponent extends CreateEntity implements OnInit, OnDestroy {
  private provisionsService = inject(ProvisionsService);
  private commonSheetsService = inject(CommonSheetsService);
  private provisionSheetsService = inject(ProvisionSheetsService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  // Note: dialog is inherited from Entity base class

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;

  currentProvision: Provision = {} as Provision;
  originalProvision: any = {};

  // UI State
  isEditMode = false;
  saving = false;
  loadingProvisionData = false;
  showRiskScoring = true;

  // Key phrases for AI
  keyPhrases: string[] = [];
  keyPhraseInput = '';

  // For backwards compatibility with pending-provision-changes.guard
  okToLeaveIntakeForm = false;

  // Default text for required action field
  readonly defaultRequiredActionText = 'Please review this provision and provide Action Summary and Action Date upon approval.';

  // Static field data
  riskDomains: string[] = [
    'Operational',
    'Clinical and Patient Safety',
    'HRSA 5A Column II',
    'HRSA 5A Column III',
    'Lease Accounting',
    'Financial',
    'Regulatory',
    'Legal'
  ];

  actionSummaries: string[] = [
    'No Action Required',
    'Action Pending',
    'Action Completed',
    'Review Required',
    'Escalation Needed'
  ];

  // Loaded from the 'provisions' Google Sheet (tab 'provisions', column A) via ProvisionSheetsService.
  provisionNames: string[] = [];

  // Loaded from CommonSheetsService
  contractTypes: string[] = [];
  riskCategories: string[] = [];

  constructor(
    public router: Router,
    private fb: FormBuilder
  ) {
    super();

    // Initialize the form with validation
    this.form = this.fb.group({
      // ID and metadata
      provNum: [''],

      // Basic info
      provName: [''],
      description: [''],
      actionCompleted: [false],

      // Ownership
      owner: [''],
      approver: [''],

      // Action information
      requiredAction: [this.defaultRequiredActionText],
      actionDate: [''],
      actionSummary: [''],
      criticalDate: [''],

      // Comments/Recommendations (rich text)
      commentsRecommendations: [''],

      // Key phrases for AI
      keyPhrases: [[]],

      // Source information
      sourceType: [''],
      sourceId: [''],
      sourceName: [''],
      contractNum: [''],
      contractFriendlyName: [''],
      linkToSource: [''],

      // Contract types and risk category
      contractTypes: [[]],
      riskCategory: [''],

      // Risk scoring
      riskDomain: [''],
      riskName: [''],
      likelihoodScore: [0],
      impactScore: [0],

      // Approved provision flag
      approvedProvision: [false],

      // Connections
      baas: [[]],
      cois: [[]],
      contracts: [[]]
    });
  }

  async ngOnInit(): Promise<void> {
    this.loadingProvisionData = true;

    try {
      const provNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load users for autocomplete. settledCache, not firstValueFrom — allUsers$
      // starts as [] and fills asynchronously, so the first emission can be empty
      // and the owner picker would stay blank for the life of the form.
      this.filteredUsers = await settledCache(this.allUsers$);

      // Load static field values
      this.contractTypes = await firstValueFrom(this.commonSheetsService.getContractTypes());
      this.riskCategories = await firstValueFrom(this.commonSheetsService.getRiskCategories());
      this.provisionNames = await firstValueFrom(this.provisionSheetsService.getProvisionNames());

      if (provNum) {
        // Edit mode - load provision by ID
        this.isEditMode = true;
        await this.loadProvisionByProvNum(provNum);
      } else if (state?.provision) {
        // Provision passed via router state
        this.isEditMode = true;
        this.currentProvision = state.provision;
        this.originalProvision = { ...state.provision };
        this.populateFormWithProvision(this.currentProvision);
      } else if (state?.sourceType && state?.entityObject) {
        // Creating from a source entity (contract, BAA, COI)
        this.isEditMode = false;
        this.setupFromSourceEntity(state.sourceType, state.entityObject);
      } else {
        // Brand new provision
        this.isEditMode = false;
        this.form.patchValue({
          provNum: Date.now().toString()
        });
      }

      // Check if this is an approved provision (from query params or state)
      if (state?.approvedProvision) {
        this.showRiskScoring = false;
        this.form.patchValue({ approvedProvision: true });
      }

      // Subscribe to form changes
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentProvision = { ...this.currentProvision, ...changes };
      });

    } finally {
      this.loadingProvisionData = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async loadProvisionByProvNum(provNum: string): Promise<void> {
    try {
      const provision = await firstValueFrom(
        this.provisionsService.getProvisionFullyHydrated(provNum)
      );

      if (provision) {
        this.currentProvision = provision;
        this.originalProvision = { ...provision };
        this.populateFormWithProvision(provision);

        // Check if it's an approved provision
        if (provision.approvedProvision) {
          this.showRiskScoring = false;
        }
      } else {
        console.error('Provision not found with provNum:', provNum);
        alert('Provision not found');
        this.router.navigate(['/securehome/provisions']);
      }
    } catch (error) {
      console.error('Error loading provision:', error);
      alert('Failed to load provision');
      this.router.navigate(['/securehome/provisions']);
    }
  }

  setupFromSourceEntity(sourceType: string, entityObject: any): void {
    const sourceData: Partial<Provision> = {
      sourceType,
      provNum: Date.now().toString()
    };

    // Set source-specific fields based on entity type
    if (sourceType === EntityType.Contract) {
      sourceData.contractNum = entityObject.contractNum;
      sourceData.contractFriendlyName = entityObject.nameOfContract;
      sourceData.sourceId = entityObject.contractId;
      sourceData.linkToSource = entityObject.linkToContract;
    } else if (sourceType === EntityType.Coi) {
      sourceData.contractNum = entityObject.coiNum;
      sourceData.sourceId = entityObject.coiId;
    } else if (sourceType === EntityType.Baa) {
      sourceData.contractNum = entityObject.baaNum;
      sourceData.sourceId = entityObject.baaId;
    }

    this.form.patchValue(sourceData);
  }

  populateFormWithProvision(provision: Provision): void {
    // Set keyPhrases array from provision
    this.keyPhrases = provision.keyPhrases || [];

    this.form.patchValue({
      provNum: provision.provNum,
      provName: provision.provName,
      description: provision.description,
      actionCompleted: provision.actionCompleted,
      owner: provision.owner,
      approver: provision.approver,
      requiredAction: provision.requiredAction || this.defaultRequiredActionText,
      actionDate: provision.actionDate ? new Date(provision.actionDate) : null,
      actionSummary: provision.actionSummary,
      criticalDate: provision.criticalDate ? new Date(provision.criticalDate) : null,
      commentsRecommendations: provision.commentsRecommendations,
      keyPhrases: provision.keyPhrases || [],
      sourceType: provision.sourceType,
      sourceId: provision.sourceId,
      sourceName: provision.sourceName,
      contractNum: provision.contractNum,
      contractFriendlyName: provision.contractFriendlyName,
      linkToSource: provision.linkToSource,
      contractTypes: provision.contractTypes || [],
      riskCategory: provision.riskCategory,
      riskDomain: provision.riskDomain,
      riskName: provision.riskName,
      likelihoodScore: provision.likelihoodScore ? parseInt(provision.likelihoodScore, 10) : 0,
      impactScore: provision.impactScore ? parseInt(provision.impactScore, 10) : 0,
      approvedProvision: provision.approvedProvision,
      baas: provision.baas || [],
      cois: provision.cois || [],
      contracts: provision.contracts || []
    });
  }

  // Validation
  isFormValid(): boolean {
    // Provision name is the only truly required field
    return !!this.form.get('provName')?.value;
  }

  getValidationMessage(): string {
    if (!this.form.get('provName')?.value) {
      return 'Provision name is required';
    }
    return '';
  }

  // Display functions for autocomplete
  displayUserFn(user: any): string {
    return user && user.friendlyName ? user.friendlyName : '';
  }

  cancelSubmit(): void {
    this.okToLeaveIntakeForm = true;
    this.router.navigateByUrl('/securehome/provisions');
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    // Check if action is not completed - show warning dialog
    const actionCompleted = this.form.get('actionCompleted')?.value;
    if (!actionCompleted) {
      const shouldContinue = await this.showNotApprovedWarningDialog();
      if (!shouldContinue) {
        return; // User clicked Cancel, stay in edit mode
      }
    }

    this.saving = true;
    this.okToLeaveIntakeForm = true;
    const provisionToSave: any = { ...this.form.value };

    // Ensure provNum exists
    if (!provisionToSave.provNum) {
      provisionToSave.provNum = Date.now().toString();
    }

    // Include keyPhrases from component state
    provisionToSave.keyPhrases = this.keyPhrases;

    // Convert dates to ISO strings
    if (provisionToSave.actionDate && provisionToSave.actionDate instanceof Date) {
      provisionToSave.actionDate = provisionToSave.actionDate.toISOString();
    }
    if (provisionToSave.criticalDate && provisionToSave.criticalDate instanceof Date) {
      provisionToSave.criticalDate = provisionToSave.criticalDate.toISOString();
    }

    // Convert scores to strings (API expectation)
    if (typeof provisionToSave.likelihoodScore === 'number') {
      provisionToSave.likelihoodScore = provisionToSave.likelihoodScore.toString();
    }
    if (typeof provisionToSave.impactScore === 'number') {
      provisionToSave.impactScore = provisionToSave.impactScore.toString();
    }

    // Extract user ID from owner object if needed
    if (provisionToSave.owner && typeof provisionToSave.owner === 'object') {
      provisionToSave.owner = provisionToSave.owner.userNum || provisionToSave.owner.email;
    }

    // Extract user ID from approver object if needed
    if (provisionToSave.approver && typeof provisionToSave.approver === 'object') {
      provisionToSave.approver = provisionToSave.approver.userNum || provisionToSave.approver.email;
    }

    this.provisionsService.writeProvision(provisionToSave).subscribe({
      next: () => {
        this.saving = false;
        // Navigate to the provision detail view
        this.router.navigate(['/securehome/provision', provisionToSave.provNum]);
      },
      error: (err) => {
        console.error('Failed to save provision:', err);
        this.saving = false;
        alert('Failed to save provision. Please try again.');
      }
    });
  }

  /**
   * Shows a warning dialog when trying to save without action completed.
   * Returns true if user clicks Continue, false if Cancel.
   */
  private showNotApprovedWarningDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const confirmed = window.confirm(
        'This provision has not yet been approved and will not be available in Secure AI reviews. "Action Completed" must be checked.\n\nClick OK to save anyway, or Cancel to go back and make changes.'
      );
      resolve(confirmed);
    });
  }

  // Risk scoring helper methods
  getRiskScoreLabel(score: number): string {
    switch (score) {
      case 1: return 'Very Low';
      case 2: return 'Low';
      case 3: return 'Medium';
      case 4: return 'High';
      case 5: return 'Very High';
      default: return 'Not Set';
    }
  }

  formatSliderLabel(value: number): string {
    return value.toString();
  }

  // Key phrase handling methods
  addKeyPhrase(event: any): void {
    const value = (event.value || '').trim();
    if (value && !this.keyPhrases.includes(value)) {
      this.keyPhrases.push(value);
      this.form.patchValue({ keyPhrases: this.keyPhrases });
    }
    // Clear the input
    if (event.chipInput) {
      event.chipInput.clear();
    }
    this.keyPhraseInput = '';
  }

  removeKeyPhrase(phrase: string): void {
    const index = this.keyPhrases.indexOf(phrase);
    if (index >= 0) {
      this.keyPhrases.splice(index, 1);
      this.form.patchValue({ keyPhrases: this.keyPhrases });
    }
  }

  onKeyPhraseInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const value = this.keyPhraseInput.trim();
      if (value && !this.keyPhrases.includes(value)) {
        this.keyPhrases.push(value);
        this.form.patchValue({ keyPhrases: this.keyPhrases });
      }
      this.keyPhraseInput = '';
    }
  }
}
